import { Rng } from '../core/rng';
import { bearingToX, bearingToZ, clamp, Vec3 } from '../core/math';
import { SHIP_CLASSES, AMMO, type AmmoId, type DifficultyId, type ShipClassId } from '../data/classes';
import { cannonMount } from '../data/ship';
import { SimEvents, type SimEvent } from './events';
import { WaveField, type SeaConditions } from './waves';
import { Wind } from './wind';
import { Weather, type WeatherKind } from './weather';
import { DayNight } from './daynight';
import type { Ship } from './ship';
import type { DeckCargo } from './deck';
import type { Battery } from './cannons';
import { Flotsam } from './floaters';
import { Vessel } from './vessel';
import { Ballistics } from './ballistics';
import { Encounters, type GroupKind } from './encounters';
import { Contacts } from './contact';
import { hostile, type Battlefield } from './captain';
import { Lookout } from './lookout';

/** Everything the player (or a test) can ask the sim to do this frame. */
export interface PlayerCommand {
  /** -1 (port) .. +1 (starboard), continuous. */
  helm: number;
  /** +1 set more sail, -1 shorten sail (edge-triggered). */
  sailDelta: number;
  firePort: boolean;
  fireStarboard: boolean;
  /** Toggle automatic sail trim. */
  toggleTrim: boolean;
  /** Manual trim input, -1..1, only used with auto trim off. */
  trim: number;
  /** Cycle the ammunition loaded next. */
  cycleAmmo: boolean;
  /** Throw grapnels at the nearest ship alongside. */
  board: boolean;
  /** Show or douse the lanterns. */
  toggleLights: boolean;
}

export function emptyCommand(): PlayerCommand {
  return { helm: 0, sailDelta: 0, firePort: false, fireStarboard: false, toggleTrim: false, trim: 0, cycleAmmo: false, board: false, toggleLights: false };
}

export interface WorldSettings {
  /** Multiplier on wave height (user "sea state" slider). */
  waveIntensity: number;
  /** Override wind speed (m/s) or null to follow the weather. */
  windSpeed: number | null;
  /** Override wind "from" bearing (radians) or null to follow the weather. */
  windFrom: number | null;
}

export interface WorldOptions {
  seed?: number;
  weather?: WeatherKind;
  hours?: number;
  windFrom?: number;
  heading?: number;
  plan?: number;
  playerClass?: ShipClassId;
  enemies?: boolean;
  difficulty?: DifficultyId;
}

export class World {
  static readonly DT = 1 / 60;

  readonly rng: Rng;
  readonly events = new SimEvents();
  readonly weather: Weather;
  readonly wind: Wind;
  readonly waves: WaveField;
  readonly day: DayNight;
  readonly vessels: Vessel[] = [];
  readonly player: Vessel;
  readonly ballistics: Ballistics;
  readonly encounters: Encounters;
  readonly contacts: Contacts;
  readonly flotsam: Flotsam;
  /** What our own lookouts have seen: the only ships the player knows about. */
  readonly lookout: Lookout;
  readonly settings: WorldSettings = { waveIntensity: 1, windSpeed: null, windFrom: null };
  readonly sea: SeaConditions = { windSpeed: 8, windX: 1, windZ: 0, swellHeight: 1, waveScale: 1, chop: 0.7 };

  time = 0;
  /** Interpolation factor between the last two sim steps, for rendering. */
  alpha = 1;
  /** Rain wetness of exposed surfaces, 0..1. */
  wetness = 0;
  /** Meteorological visibility, m. */
  visibility = 9000;
  /** Game speed: sim seconds per real second (0 pauses). Everything runs on it. */
  speed = 1;
  private accumulator = 0;
  private lastHalfHour = -1;
  private recent: SimEvent[] = [];
  private recentTimes: number[] = [];
  private readonly sunkTimers = new Map<number, number>();
  private stepCount = 0;

  constructor(opts: WorldOptions = {}) {
    this.rng = new Rng(opts.seed ?? 20260925);
    const windFrom = opts.windFrom ?? 1.05; // ENE trades
    this.weather = new Weather(new Rng(this.rng.int(1 << 30)), opts.weather ?? 'fair', windFrom);
    this.wind = new Wind(windFrom, this.weather.params.windSpeed);
    const toX = -bearingToX(windFrom), toZ = -bearingToZ(windFrom);
    this.waves = new WaveField(this.rng.int(1 << 30), Math.atan2(toZ, toX));
    this.day = new DayNight(opts.hours ?? 16.2);
    this.updateSea();
    this.waves.settle(this.sea);
    this.player = new Vessel(SHIP_CLASSES[opts.playerClass ?? 'frigate'], 'player', 'Tidebreaker', 0, 0, opts.heading ?? 3.0, this.rng.int(1 << 30), 'normal', opts.plan ?? 3);
    this.vessels.push(this.player);
    // Start under way.
    const h = this.player.ship.heading;
    this.player.ship.vel.set(Math.sin(h) * 3.2, 0, -Math.cos(h) * 3.2);
    this.ballistics = new Ballistics(this.rng.int(1 << 30));
    this.encounters = new Encounters(this.rng.int(1 << 30));
    this.encounters.enabled = opts.enemies ?? false;
    this.encounters.difficulty = opts.difficulty ?? 'normal';
    this.contacts = new Contacts(this.rng.int(1 << 30));
    this.flotsam = new Flotsam(new Rng(this.rng.int(1 << 30)), 26, 0, 0);
    this.lookout = new Lookout(this.rng.int(1 << 30));
    this.lastHalfHour = Math.floor(this.day.hours * 2);
  }

  // Back-compatible handles on the player's ship.
  get ship(): Ship {
    return this.player.ship;
  }
  get battery(): Battery {
    return this.player.battery;
  }
  get cargo(): DeckCargo {
    return this.player.cargo;
  }

  /** Add a ship with an AI captain (tests, debugging, scenarios). */
  spawn(kind: GroupKind, distance?: number): Vessel[] {
    const vs = this.encounters.spawn(kind, this.player, distance);
    this.vessels.push(...vs);
    return vs;
  }

  /** Advance by a real frame time using fixed steps; returns render alpha. */
  update(frameDt: number, cmd: PlayerCommand): number {
    this.applyCommand(cmd);
    this.accumulator += Math.min(frameDt, 0.25) * this.speed;
    let steps = 0;
    // Never more than a few steps a frame (more when running fast): a slow
    // frame slows the world rather than spiralling.
    const cap = Math.min(40, Math.ceil(4 * Math.max(1, this.speed)));
    while (this.accumulator >= World.DT && steps < cap) {
      this.step(World.DT, cmd);
      this.accumulator -= World.DT;
      steps++;
    }
    if (steps === cap) this.accumulator = 0;
    this.alpha = clamp(this.accumulator / World.DT, 0, 1);
    return this.alpha;
  }

  private applyCommand(cmd: PlayerCommand): void {
    const p = this.player;
    const rig = p.ship.sails;
    if (p.fighting) {
      if (cmd.sailDelta !== 0) rig.setPlan(clamp(rig.plan + cmd.sailDelta, 0, rig.plans.length - 1));
      if (cmd.toggleTrim) rig.autoTrim = !rig.autoTrim;
      rig.manualTrim = clamp(cmd.trim, -1, 1);
      // A fire order beats to quarters; it's carried out once the gun crews
      // are in place (or given up after a while).
      if (cmd.firePort) (this.fireOrder[0] = 12), (this.firing[0] = false);
      if (cmd.fireStarboard) (this.fireOrder[1] = 12), (this.firing[1] = false);
      if (cmd.firePort || cmd.fireStarboard) this.quarters = 120;
      if (cmd.cycleAmmo) {
        const carried = p.cls.ammo;
        p.battery.ammo = carried[(carried.indexOf(p.battery.ammo) + 1) % carried.length] as AmmoId;
      }
      if (cmd.board) this.playerBoard = 4;
    }
    if (cmd.toggleLights) p.lights = !p.lights;
    cmd.sailDelta = 0;
    cmd.firePort = false;
    cmd.fireStarboard = false;
    cmd.toggleTrim = false;
    cmd.cycleAmmo = false;
    cmd.board = false;
    cmd.toggleLights = false;
  }

  /** Seconds left on the player's order to board. */
  private playerBoard = 0;
  /** Outstanding fire orders (port, starboard), seconds before they lapse. */
  private readonly fireOrder = [0, 0];
  /** The broadside has begun: the rest fire as their crews come up. */
  private readonly firing = [false, false];
  /** Seconds the player's crew stay at quarters without an enemy in sight. */
  quarters = 0;

  private updateSea(): void {
    const w = this.weather.params;
    this.sea.windSpeed = this.wind.meanSpeed;
    // Waves follow the mean wind direction, not every gust.
    this.sea.windX = -bearingToX(this.wind.meanFrom);
    this.sea.windZ = -bearingToZ(this.wind.meanFrom);
    this.sea.swellHeight = w.swellHeight * Math.sqrt(this.settings.waveIntensity);
    this.sea.waveScale = w.waveScale * this.settings.waveIntensity;
    this.sea.chop = w.chop;
    // Koschmieder: visibility from haze, fog and rain extinction.
    const ext = 3.2e-5 + w.haze * 2.4e-4 + w.rain * 3e-4 + w.fog * w.fog * 0.012 + w.rain * 9e-4;
    this.visibility = clamp(3.9 / ext, 150, 14000);
  }

  step(dt: number, cmd: PlayerCommand): void {
    this.time += dt;
    this.day.update(dt);
    const player = this.player;
    this.weather.update(dt, player.ship.com.x, player.ship.com.z, this.events);
    const w = this.weather.params;
    this.wind.update(dt, this.settings.windSpeed ?? w.windSpeed, this.settings.windFrom ?? this.weather.windFrom, w.gustiness);
    this.updateSea();
    this.waves.update(dt, this.sea);

    const mark = this.events.mark;
    const bf: Battlefield = {
      vessels: this.vessels, time: this.time, wind: this.wind, visibility: this.visibility, daylight: this.day.daylight,
      seaState: this.waves.significantHeight, events: this.events, recent: this.recent, attackers: this.attackerMap(),
    };
    this.playerSituation();
    const ctx = { waves: this.waves, wind: this.wind, ballistics: this.ballistics, events: this.events, rain: w.rain, time: this.time };
    this.stepCount++;
    for (const v of this.vessels) {
      if (!v.alive) continue;
      // Level of detail: ships far from ours are simulated at a coarser
      // step (the hull integrator is stable to 1/15 s even in a storm) and
      // their cloth less often.
      const d = Math.hypot(v.ship.com.x - player.ship.com.x, v.ship.com.z - player.ship.com.z);
      const every = v === player ? 1 : d > 2500 ? 4 : d > 700 ? 2 : 1;
      if ((this.stepCount + v.id) % every !== 0) continue;
      const vdt = dt * every;
      const helm = v === player ? (player.fighting || player.state === 'sinking' ? cmd.helm : 0) : v.captain ? v.captain.update(vdt, bf) : 0;
      v.ship.sails.clothSkip = v === player ? 0 : d > 1500 ? 3 : d > 250 ? 1 : 0;
      v.update(vdt, ctx, helm);
      v.lights = v === player ? v.lights : this.day.daylight < 0.5;
    }
    this.ballistics.update(dt, this.vessels, this.waves, this.wind, this.events);
    this.playerBoard = Math.max(0, this.playerBoard - dt);
    this.quarters = Math.max(0, this.quarters - dt);
    for (const [i, side] of [[0, -1], [1, 1]] as const) {
      if (this.fireOrder[i]! <= 0) continue;
      this.fireOrder[i]! -= dt;
      const manned = player.battery.guns.filter((g) => g.spec.side === side && g.loaded && !g.destroyed && g.fireAt < 0 && g.crew > 0.2).length;
      const loaded = player.battery.ready(side);
      if (this.firing[i]) {
        if (manned > 0) player.battery.fire(side);
        if (loaded === 0) this.fireOrder[i] = 0;
      } else if (manned > 0 && (manned >= loaded * 0.75 || this.fireOrder[i]! < 6)) {
        player.battery.fire(side);
        this.firing[i] = true;
      }
    }
    this.contacts.update(dt, this.vessels, this.events, (a, b) => this.wantsToBoard(a, b));
    this.lookout.update(dt, player, this.vessels, {
      visibility: this.visibility, daylight: this.day.daylight, seaState: this.waves.significantHeight, time: this.time,
    }, this.events);

    // The sea keeps what it takes: wreckage where ships went down.
    for (let i = this.vessels.length - 1; i >= 0; i--) {
      const v = this.vessels[i]!;
      if (v.state !== 'sunk' || v === player) continue;
      const t = (this.sunkTimers.get(v.id) ?? 0) + dt;
      this.sunkTimers.set(v.id, t);
      if (t > 60) this.vessels.splice(i, 1);
    }
    for (const e of this.events.since(mark)) {
      if (e.type === 'shipSunk') {
        const v = this.vessels.find((o) => o.id === e.vessel);
        const size = v ? v.ship.sx : 1;
        this.flotsam.spawnWreck(e.x, e.z, 30 * size, Math.round(30 * size));
      } else if (e.type === 'shipSinking') {
        const v = this.vessels.find((o) => o.id === e.vessel);
        if (v) this.flotsam.spawnWreck(e.x, e.z, 20 * v.ship.sx, 8);
      } else if (e.type === 'explosion' && e.vessel >= 0 && e.size > 3) {
        this.flotsam.spawnWreck(e.x, e.z, 25, 20);
      }
    }
    const { add, remove } = this.encounters.update(dt, player, this.vessels);
    for (const v of remove) {
      const i = this.vessels.indexOf(v);
      if (i > 0) this.vessels.splice(i, 1);
    }
    this.vessels.push(...add);

    const rainWet = w.rain > 0.05 ? 1 : 0;
    const target = Math.max(rainWet, player.ship.deckWater);
    this.wetness += (target - this.wetness) * (1 - Math.exp(-dt * (target > this.wetness ? 0.25 : 0.01)));
    this.flotsam.update(dt, this.waves, this.wind, player.ship.com.x, player.ship.com.z, player.ship.vel.x, player.ship.vel.z);
    // Captains perceive on their own schedule: keep about a second of events.
    for (const e of this.events.since(mark)) this.recent.push(e);
    this.recentTimes.push(...this.events.since(mark).map(() => this.time));
    while (this.recentTimes.length && this.recentTimes[0]! < this.time - 1.2) {
      this.recentTimes.shift();
      this.recent.shift();
    }

    // Ship's bell every half hour: 1..8 strikes through each four-hour watch.
    const hh = Math.floor(this.day.hours * 2);
    if (hh !== this.lastHalfHour) {
      this.lastHalfHour = hh;
      this.events.push({ type: 'bell', strikes: (hh % 8) || 8 });
    }
  }

  private wantsToBoard(a: Vessel, b: Vessel): boolean {
    if (!hostile(a, b) || !a.fighting) return false;
    if (a === this.player) return this.playerBoard > 0;
    return a.captain?.mode === 'board' && a.captain.target === b.id;
  }

  /** Captains attacking each target (for spreading out around her). */
  private attackerMap(): Map<number, number[]> {
    const m = new Map<number, number[]>();
    for (const v of this.vessels) {
      const c = v.captain;
      if (!c || c.target < 0 || !v.fighting) continue;
      const list = m.get(c.target) ?? [];
      list.push(v.id);
      m.set(c.target, list);
    }
    return m;
  }

  /**
   * The player's own crew: beat to quarters when an enemy is near, and the
   * gun captains lay their guns on whatever is on their side.
   */
  private playerSituation(): void {
    const p = this.player;
    const ship = p.ship;
    let nearest = Infinity, side = 0;
    for (const v of this.vessels) {
      if (v === p || !v.alive || !hostile(v, p) && !hostile(p, v)) continue;
      const d = Math.hypot(v.ship.com.x - ship.com.x, v.ship.com.z - ship.com.z);
      if (d < this.visibility * (0.3 + 0.7 * this.day.daylight) + 300 && d < nearest) {
        nearest = d;
        const l = ship.worldToLocal(v.ship.com, tmp);
        side = l.z > 0 ? 1 : -1;
      }
      // Lay each side's guns on the nearest ship bearing on it.
      const l = ship.worldToLocal(v.ship.com, tmp);
      const s = (l.z > 0 ? 1 : -1) as 1 | -1;
      const off = Math.atan2(l.x, Math.abs(l.z));
      if (Math.abs(off) > 0.5 || d > 1600) continue;
      const ammo = AMMO[p.battery.ammo];
      const k = ammo.drag / Math.cbrt(p.battery.weight);
      const tof = (Math.exp(k * d) - 1) / (k * ammo.speed);
      // Handspikes: train on where she'll be when the shot gets there.
      const lead = ship.worldToLocal(tmp.set(v.ship.com.x + (v.ship.vel.x - ship.vel.x) * tof, v.ship.com.y, v.ship.com.z + (v.ship.vel.z - ship.vel.z) * tof), tmp);
      p.battery.setTrain(s, Math.atan2(lead.x, Math.abs(lead.z)));
      const drop = 0.5 * 9.81 * tof * tof;
      const muzzleH = cannonMount(p.battery.guns[0]!.spec).y * ship.sy;
      const aim = p.battery.ammo === 'chain' ? 14 * v.ship.sy : 0.8;
      // The crew's estimate of range is only so good.
      const est = 1 + Math.sin(this.time * 0.37 + d) * 0.03 * (1.2 - p.crew.gunnery);
      p.battery.setElevation(s, Math.atan2(drop * est + aim - muzzleH, d) + ship.heel * s * 0.9);
    }
    p.battle = nearest < 3000 || this.quarters > 0;
    p.threatSide = nearest < 3000 ? side : 0;
    p.threatRange = nearest;
    p.gunSide = side;
  }
}

const tmp = new Vec3();
